import React, { useState, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Switch, Route, useHistory, Redirect } from 'react-router-dom';
import { Subheader } from '@m12s/component-library';
import styled from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { useEmbeddedEffect } from '@machinemetrics/mm-react-embeddable';

import { Downtime } from 'components/Downtime';
import { Alarms } from 'components/Alarms';
import { Utilization } from 'components/Utilization';
import MainLayout from 'components/Layouts/MainLayout';
import { CycleTime } from 'components/CycleTime';
import { CycleCountStatus } from 'components/CycleCountStatus';
import { MachineStatus } from 'components/MachineStatus';
import { OEE } from 'components/OEE';
import { PartsGoal } from 'components/PartsGoal';
import Routes from 'lib/utils/routes';
import { CustomTab } from 'components/CustomTab';
import { getMachine } from 'lib/selectors/getMachine';
import { getScopeStart } from 'lib/selectors/getScopeStart';
import { getHomeTabRoute, getVisibleTabs } from 'lib/selectors/getTabs';
import { getCustomTabs } from 'lib/selectors/getCustomTab';
import { getIsApmERPJobFlow } from 'lib/selectors/getIsApmERPJobFlow';
import { APM_OPDASH_HOST, LABOR_TICKET_HOST, REST_URI } from 'lib/appConfig';
import session from 'lib/api/links/session';
import { getAuthHeaders } from 'lib/api/links/helpers';
import MMEmbeddableZoneWithLoader from 'components/MMEmbeddableZoneWithLoader';
import { SelectJobsDrawer } from 'components/SelectJobsDrawer';
import MMDrawer from 'components/MMDrawer';
import { getLatestOperator } from 'lib/selectors/getLatestOperator';
import {
  actionOpenLaborTicketModal,
  actionSetEmbeddedLaborTicket,
} from 'lib/actions';
import Tab from './Tab';

const ContentContainer = styled(MainLayout.Container)`
  background-color: #fafbfc;
`;

const MachineContent = ({ tab }) => {
  const machine = useSelector(getMachine);
  const machineRef = machine?.machineRef;
  const scopeStart = useSelector(getScopeStart);
  const latestOperator = useSelector(getLatestOperator);
  const latestOperatorErpId = latestOperator?.erpId;
  const dispatch = useDispatch();

  const [selectJobsOpen, setSelectJobsOpen] = useState(false);
  const [selectJobsEventId, setSelectJobsEventId] = useState(null);
  const [refreshToken, setRefreshToken] = useState(0);
  const [dismissedItemIds, setDismissedItemIds] = useState([]);

  useEffect(() => {
    const handler = (e) => {
      const id = e.detail?.dismissItemId;
      if (id) {
        setDismissedItemIds((prev) => {
          return [...prev, id];
        });
      }
      setRefreshToken((t) => {
        return t + 1;
      });
    };
    window.addEventListener('apm:opdash-refresh', handler);
    return () => {
      window.removeEventListener('apm:opdash-refresh', handler);
    };
  }, []);

  // Run Details flyup state
  const [runDetailsOpen, setRunDetailsOpen] = useState(false);
  const [runDetailsContext, setRunDetailsContext] = useState(null);

  useEmbeddedEffect(
    'apm-opdash-display',
    'apm-opdash:select-different-job',
    useCallback((data) => {
      setSelectJobsEventId(data?.eventId ?? null);
      setSelectJobsOpen(true);
    }, [])
  );

  // Listen for CONFIRMED card tap → open run details flyup
  useEmbeddedEffect(
    'apm-opdash-display',
    'apm-opdash:view-run-details',
    useCallback((data) => {
      setRunDetailsContext(data);
      setRunDetailsOpen(true);
    }, [])
  );

  // Listen for close from run details MFE
  useEmbeddedEffect(
    'apm-opdash-run-details',
    'apm-opdash-run-details:close',
    useCallback(() => {
      setRunDetailsOpen(false);
      setRunDetailsContext(null);
    }, [])
  );

  // Listen for confirmed from run details → close and refresh opdash cards
  useEmbeddedEffect(
    'apm-opdash-run-details',
    'apm-opdash-run-details:confirmed',
    useCallback(() => {
      setRunDetailsOpen(false);
      setRunDetailsContext(null);
      setRefreshToken((t) => {
        return t + 1;
      });
    }, [])
  );

  // Listen for change-job from run details → close details, open select-jobs
  useEmbeddedEffect(
    'apm-opdash-run-details',
    'apm-opdash-run-details:change-job',
    useCallback((data) => {
      setRunDetailsOpen(false);
      setRunDetailsContext(null);
      setSelectJobsEventId(data?.eventId ?? null);
      setSelectJobsOpen(true);
    }, [])
  );

  // Listen for labor ticket click from run details → close details, open LT form
  useEmbeddedEffect(
    'apm-opdash-run-details',
    'apm-opdash-run-details:open-ticket',
    useCallback(
      (data) => {
        if (data?.laborTicketRef) {
          setRunDetailsOpen(false);
          setRunDetailsContext(null);
          dispatch(
            actionSetEmbeddedLaborTicket({
              laborTicketRef: data.laborTicketRef,
              personId: latestOperatorErpId,
            })
          );
          dispatch(actionOpenLaborTicketModal('submit'));
        }
      },
      [dispatch, latestOperatorErpId]
    )
  );

  // Split-change-job: stores split context while user picks a new job
  const [pendingSplitChangeJob, setPendingSplitChangeJob] = useState(null);

  useEmbeddedEffect(
    'apm-opdash-run-details',
    'apm-opdash-run-details:split-change-job',
    useCallback((data) => {
      setRunDetailsOpen(false);
      setRunDetailsContext(null);
      setPendingSplitChangeJob(data);
      setSelectJobsEventId(data?.eventId ?? null);
      setSelectJobsOpen(true);
    }, [])
  );

  // Split flow: close run details, open LT form with splitTime context
  const [splitEventFormOpen, setSplitEventFormOpen] = useState(false);
  const [splitEventFormContext, setSplitEventFormContext] = useState(null);

  useEmbeddedEffect(
    'apm-opdash-run-details',
    'apm-opdash-run-details:split-submit',
    useCallback(
      (data) => {
        setRunDetailsOpen(false);
        setRunDetailsContext(null);
        setRefreshToken((t) => {
          return t + 1;
        });

        if (data?.laborTicketRef) {
          // CONFIRMED case: LT exists — open standard form with splitTime
          dispatch(
            actionSetEmbeddedLaborTicket({
              laborTicketRef: data.laborTicketRef,
              personId: latestOperatorErpId,
              splitTime: data.splitTime,
            })
          );
          dispatch(actionOpenLaborTicketModal('submit'));
        } else if (data?.eventId && data?.machineRef) {
          // MATCHED case: no LT yet — open event form with splitTime
          setSplitEventFormContext({
            eventId: data.eventId,
            eventRefs: data.eventRefs,
            machineRef: data.machineRef,
            orderNumber: data.orderNumber,
            partNumber: data.partNumber,
            sequenceNumber: data.sequenceNumber,
            personId: latestOperatorErpId,
            workOrderOperationRef: data.workOrderOperationRef,
            splitTime: data.splitTime,
            clockIn: data.clockIn,
            type: 'production',
            opened: true,
            openedAt: data.splitTime,
            isAPMControlled: true,
          });
          setSplitEventFormOpen(true);
        }
      },
      [dispatch, latestOperatorErpId]
    )
  );

  // Split event form events
  useEmbeddedEffect(
    'labor-ticket-split-event-form',
    'labor-ticket-form:close',
    useCallback(() => {
      setSplitEventFormOpen(false);
      setSplitEventFormContext(null);
    }, [])
  );

  useEmbeddedEffect(
    'labor-ticket-split-event-form',
    'labor-ticket-form:submit',
    useCallback(() => {
      setSplitEventFormOpen(false);
      setSplitEventFormContext(null);
      setRefreshToken((t) => {
        return t + 1;
      });
    }, [])
  );

  const handleJobSelected = useCallback(
    async (data) => {
      const selectedJob = data?.jobs?.[0];
      if (!selectedJob) return;

      const selectedWooRef = selectedJob.workOrderOperationRef;

      if (pendingSplitChangeJob) {
        // Split-change-job flow: pass the new WOO ref to the labor ticket form
        const splitCtx = pendingSplitChangeJob;
        setPendingSplitChangeJob(null);
        setSelectJobsOpen(false);
        setSelectJobsEventId(null);

        if (splitCtx.laborTicketRef) {
          dispatch(
            actionSetEmbeddedLaborTicket({
              laborTicketRef: splitCtx.laborTicketRef,
              personId: latestOperatorErpId,
              splitTime: splitCtx.splitTime,
              newWorkOrderOperationRef: selectedWooRef,
            })
          );
          dispatch(actionOpenLaborTicketModal('submit'));
        } else if (splitCtx.eventId && splitCtx.machineRef) {
          setSplitEventFormContext({
            eventId: splitCtx.eventId,
            eventRefs: splitCtx.eventRefs,
            machineRef: splitCtx.machineRef,
            personId: latestOperatorErpId,
            workOrderOperationRef: splitCtx.workOrderOperationRef,
            splitTime: splitCtx.splitTime,
            clockIn: splitCtx.clockIn,
            type: 'production',
            opened: true,
            openedAt: splitCtx.splitTime,
            isAPMControlled: true,
            newWorkOrderOperationRef: selectedWooRef,
          });
          setSplitEventFormOpen(true);
        }
      } else {
        // Normal flow: confirm the program event with the selected WOO
        const eventId = data?.eventId ?? selectJobsEventId;
        const apiKey = await session.getApiKey();
        try {
          await fetch(
            `${REST_URI}/labor-ticket/${machineRef}/program-events/${eventId}/confirm`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders(apiKey),
              },
              body: JSON.stringify({
                workOrderOperationRef: selectedWooRef,
              }),
            }
          );
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('Failed to confirm job:', err);
        }
        setSelectJobsOpen(false);
        setSelectJobsEventId(null);
      }

      setRefreshToken((t) => {
        return t + 1;
      });
    },
    [
      dispatch,
      latestOperatorErpId,
      machineRef,
      pendingSplitChangeJob,
      selectJobsEventId,
    ]
  );

  const handleSelectJobsClose = useCallback(() => {
    setSelectJobsOpen(false);
    setSelectJobsEventId(null);
    setPendingSplitChangeJob(null);
    // Bump refresh token so opdash display refetches program events
    setRefreshToken((t) => {
      return t + 1;
    });
  }, []);

  const tabIdentifier = tab.id;
  switch (tabIdentifier) {
    case 'cycleCount':
      return <CycleCountStatus />;
    case 'jobs':
      return (
        <div
          style={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
          }}
        >
          <MMEmbeddableZoneWithLoader
            id="apm-opdash-display"
            src={APM_OPDASH_HOST}
            path="/"
            context={{
              machineRef: machine?.machineRef,
              machineId: machine?.id,
              start: scopeStart,
              erpResources: machine?.erpResources,
              refreshToken,
              dismissedItemIds,
            }}
          />
          <SelectJobsDrawer
            isOpen={selectJobsOpen}
            onClose={handleSelectJobsClose}
            eventId={selectJobsEventId}
            onJobSelected={handleJobSelected}
          />
          <MMDrawer
            isOpen={runDetailsOpen}
            onClose={() => {
              setRunDetailsOpen(false);
              setRunDetailsContext(null);
            }}
            title="Production Run Details"
          >
            <MMEmbeddableZoneWithLoader
              id="apm-opdash-run-details"
              src={APM_OPDASH_HOST}
              path="/run-details"
              context={{
                machineRef: machine?.machineRef,
                start: scopeStart,
                erpResources: machine?.erpResources,
                ...runDetailsContext,
              }}
            />
          </MMDrawer>
          {/* Split event form drawer (MATCHED case — no existing LT) */}
          <MMDrawer
            isOpen={splitEventFormOpen}
            onClose={() => {
              setSplitEventFormOpen(false);
              setSplitEventFormContext(null);
            }}
            title="Submit work"
          >
            {splitEventFormContext && (
              <MMEmbeddableZoneWithLoader
                id="labor-ticket-split-event-form"
                src={LABOR_TICKET_HOST}
                path={`/program-event/${splitEventFormContext.machineRef}/${
                  splitEventFormContext.eventId
                }?splitTime=${encodeURIComponent(
                  splitEventFormContext.splitTime
                )}`}
                context={splitEventFormContext}
              />
            )}
          </MMDrawer>
        </div>
      );
    case 'partCount':
      return <MachineStatus />;
    case 'utilization':
      return <Utilization />;
    case 'downtime':
      return <Downtime />;
    case 'alarms':
      return <Alarms />;
    case 'partsGoal':
      return <PartsGoal />;
    case 'cycleTime':
      return <CycleTime />;
    case 'oee':
      return <OEE />;
    default:
      return null;
  }
};

const MachineMain = ({ tab: currentTab }) => {
  const history = useHistory();
  const machine = useSelector(getMachine);
  const baseTabs = useSelector(getVisibleTabs);
  const scopeStart = useSelector(getScopeStart);
  const baseHomeTabRoute = useSelector(getHomeTabRoute);
  const machineId = machine?.id;
  const customTabs = useSelector(getCustomTabs);
  const isApmERPJobFlow = useSelector(getIsApmERPJobFlow);
  const windowPath = window.location.pathname;

  // For APM ERP machines, inject a "Jobs" tab at the front of the list
  const tabs = useMemo(() => {
    if (isApmERPJobFlow) {
      return [{ id: 'jobs', enabled: true }, ...baseTabs];
    }
    return baseTabs;
  }, [baseTabs, isApmERPJobFlow]);

  // Recompute home route based on the (possibly augmented) tab list
  const homeTabRoute = useMemo(() => {
    if (tabs.length && machineId) {
      return Routes.machineIdHomeTabPath(machineId, tabs[0].id);
    }
    return baseHomeTabRoute;
  }, [tabs, machineId, baseHomeTabRoute]);

  if (!scopeStart || !homeTabRoute) {
    return null;
  }

  return (
    <>
      <Subheader
        defaultValue={currentTab}
        onChange={(value) => {
          return history.push(Routes.machineIdHomeTabPath(machine.id, value));
        }}
      >
        {tabs.length > 1 &&
          tabs.map((tab) => {
            return <Tab key={tab.id} tab={tab} />;
          })}
      </Subheader>
      <ContentContainer>
        {customTabs.map((customTab) => {
          return (
            <CustomTab
              key={customTab.id}
              tab={customTab}
              base={Routes.machineIdHomeTabPath(machine.id, customTab.id)}
              hidden={
                windowPath !==
                Routes.machineIdHomeTabPath(machine.id, customTab.id)
              }
            />
          );
        })}
        <Switch>
          {tabs.map((tab) => {
            return (
              <Route
                key={tab.id}
                path={Routes.machineIdHomeTabPath(machine.id, tab.id)}
              >
                <MachineContent tab={tab} />
              </Route>
            );
          })}
          <Route path={Routes.machineIdHomePath(machine.id)}>
            <Redirect to={homeTabRoute} />
          </Route>
        </Switch>
      </ContentContainer>
    </>
  );
};

MachineMain.propTypes = {
  tab: PropTypes.string.isRequired,
};

export default MachineMain;
